/**
 * The two-step quote request form (theme view pages.quote, shared by the modern themes).
 *
 * Step 1 ("Your trip") follows the chosen service's form type (airport, tour, event or
 * other, see form-modes.js): only that type's fields are shown and enabled, so only
 * they are checked and sent. Tours add a day-by-day plan, posted as
 * segments[i][day_number|from_location|…|distance_km]; "other" services add a short
 * formatted description (details_html, rich-editor.js); tours and other services take
 * files (attachments[], dropzone.js). Step 2 is the client's details. The day and leg
 * markup comes from <template>s in the page, so every label is translated server-side.
 */
import { attachPlaces } from './places';
import { applyFormMode, normaliseMode } from './form-modes';
import { initDropzones } from './dropzone';
import { initRichEditors } from './rich-editor';

const $ = (selector, root = document) => root.querySelector(selector);
const $$ = (selector, root = document) => [...root.querySelectorAll(selector)];

function haversine(lat1, lon1, lat2, lon2) {
    const R = 6371;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a = Math.sin(dLat / 2) ** 2 + Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function initQuoteWizard() {
    const form = document.getElementById('quoteForm');
    if (!form) {
        return;
    }

    let config = {};
    try {
        config = JSON.parse($('#quote-config')?.textContent || '{}');
    } catch (e) {
        config = {};
    }

    /* ---------- Steps ---------- */
    const steps = $$('[data-step]', form);
    const indicators = $$('[data-step-indicator]');
    const show = (n) => {
        steps.forEach((el) => { el.hidden = el.dataset.step !== String(n); });
        indicators.forEach((el) => {
            const i = Number(el.dataset.stepIndicator);
            el.classList.toggle('is-active', i === n);
            el.classList.toggle('is-done', i < n);
        });
        const top = form.getBoundingClientRect().top + window.scrollY - 110;
        if (window.scrollY > top) {
            window.scrollTo({ top, behavior: 'smooth' });
        }
    };
    const markInvalid = (field) => {
        field.classList.add('is-invalid');
        field.closest('.ui-field')?.classList.add('is-invalid');
        field.addEventListener('input', () => {
            field.classList.remove('is-invalid');
            field.closest('.ui-field')?.classList.remove('is-invalid');
        }, { once: true });
    };
    const visible = (el) => !el.closest('[hidden]');
    // Checks the browser cannot make: a description written in the editor, a tour with at least one drive.
    const customInvalid = () => {
        let first = null;
        $$('[data-rich-editor]', form).forEach((editor) => {
            const error = $('[data-editor-error]', editor);
            const missing = visible(editor) && editor.dataset.required === '1' && editor.richEditor?.isEmpty();
            editor.classList.toggle('is-invalid', !!missing);
            if (error) { error.hidden = !missing; }
            if (missing && !first) { first = () => editor.richEditor.focus(); }
        });
        const daysError = $('[data-days-error]', form);
        const noDays = form.dataset.mode === 'tour' && !!daysContainer && !$('.service-item', daysContainer);
        if (daysError) { daysError.hidden = !noDays; }
        if (noDays && !first) { first = () => $('[data-add-day]', form)?.focus(); }
        return first;
    };
    const stepOneValid = () => {
        let firstInvalid = null;
        $$('input, select, textarea', steps[0]).forEach((field) => {
            if (field.disabled || field.type === 'hidden' || field.closest('[hidden]')) {
                return;
            }
            if (!field.checkValidity()) {
                markInvalid(field);
                firstInvalid = firstInvalid || field;
            }
        });
        if (firstInvalid) {
            firstInvalid.reportValidity();
            firstInvalid.focus();
            return false;
        }
        const focusCustom = customInvalid();
        if (focusCustom) {
            focusCustom();
            return false;
        }
        return true;
    };
    $$('[data-step-next]', form).forEach((b) => b.addEventListener('click', () => { if (stepOneValid()) { show(2); } }));
    $$('[data-step-back]', form).forEach((b) => b.addEventListener('click', () => show(1)));
    // After a server error, open the step that holds the first message.
    const firstError = $$('.is-invalid, .ui-error', form).find((el) => !el.hidden && (el.classList.contains('is-invalid') || el.textContent.trim() !== ''));
    if (firstError && firstError.closest('[data-step="2"]')) {
        show(2);
    }

    /* ---------- Dates ---------- */
    const pickup = $('#pickup_datetime', form);
    const ret = $('#return_datetime', form);
    const serviceEnd = $('#service_end_datetime', form);
    const roundTrip = $('#is_round_trip', form);
    let pickupPicker = null;
    let returnPicker = null;
    let endPicker = null;
    if (typeof window.flatpickr === 'function') {
        const base = { enableTime: true, dateFormat: 'd/m/Y H:i', time_24hr: true, minDate: 'today', disableMobile: true, locale: { firstDayOfWeek: 1 } };
        if (ret) {
            returnPicker = window.flatpickr(ret, base);
        }
        if (serviceEnd) {
            endPicker = window.flatpickr(serviceEnd, base);
        }
        if (pickup) {
            pickupPicker = window.flatpickr(pickup, {
                ...base,
                onChange: (dates) => {
                    if (dates[0]) {
                        returnPicker?.set('minDate', dates[0]);
                        endPicker?.set('minDate', dates[0]);
                    }
                },
            });
        }
    }

    // The return date counts only for a round trip, and only where the service has one.
    const syncReturn = () => {
        if (!ret) {
            return;
        }
        const off = !roundTrip || roundTrip.disabled || !roundTrip.checked;
        ret.disabled = off;
        ret.required = !off;
        // Shown only for a round trip.
        const block = ret.closest('[data-return-block]');
        if (block) {
            block.hidden = off;
        }
        if (!off && returnPicker) {
            returnPicker.set('minDate', pickupPicker?.selectedDates[0] || 'today');
        }
    };
    roundTrip?.addEventListener('change', () => {
        if (!roundTrip.checked && ret) {
            ret.value = '';
            returnPicker?.clear();
        }
        syncReturn();
    });

    /* ---------- Service form types ---------- */
    const serviceTabs = $$('input[name="service_type"][data-mode]', form);
    const daysContainer = $('#days-container', form);
    let dayCounter = 0;
    const legCounter = {};

    const applyMode = (mode) => {
        applyFormMode(form, mode);
        syncReturn();
        if (mode === 'tour' && daysContainer && !$('.day-item', daysContainer)) {
            addDay(null, [firstDrive()]);
        }
    };
    // A new plan starts with the trip itself as the first drive of day 1.
    const firstDrive = () => {
        const value = (id) => $(`#${id}`, form)?.value || '';
        const time = (value('pickup_datetime').match(/\s(\d{2}:\d{2})$/) || [])[1] || '';
        return {
            // A start date without a time (the planner's) arrives as 00:00: the visitor sets it.
            pickup_time: time === '00:00' ? '' : time,
            pickup_location: value('pickup_location'),
            pickup_latitude: value('pickup_latitude'),
            pickup_longitude: value('pickup_longitude'),
            dropoff_location: value('dropoff_location'),
            dropoff_latitude: value('dropoff_latitude'),
            dropoff_longitude: value('dropoff_longitude'),
        };
    };
    // The chosen tab (one service per form type) decides the form.
    const currentMode = () => {
        const checked = serviceTabs.find((tab) => tab.checked);
        return checked ? normaliseMode(checked.dataset.mode) : (form.dataset.mode || 'airport');
    };
    serviceTabs.forEach((tab) => tab.addEventListener('change', () => applyMode(currentMode())));

    /* ---------- Multi-day planner ---------- */
    const dayTpl = $('#tpl-day');
    const legTpl = $('#tpl-leg');
    const fromTemplate = (tpl, replacements) => {
        let html = tpl.innerHTML;
        Object.entries(replacements).forEach(([key, value]) => { html = html.split(key).join(String(value)); });
        const wrap = document.createElement('div');
        wrap.innerHTML = html.trim();
        return wrap.firstElementChild;
    };

    const recalc = () => {
        let total = 0;
        $$('.day-item', form).forEach((day) => {
            let dayKm = 0;
            $$('.service-item', day).forEach((leg) => {
                const v = (n) => leg.querySelector(`input[name$="[${n}]"]`)?.value;
                const [a, b, c, d] = ['pickup_latitude', 'pickup_longitude', 'dropoff_latitude', 'dropoff_longitude'].map(v);
                const out = $('.service-distance', leg);
                if (a && b && c && d) {
                    const km = haversine(+a, +b, +c, +d);
                    dayKm += km;
                    if (out) { out.textContent = `${km.toFixed(1)} km`; }
                } else if (out) {
                    out.textContent = '— km';
                }
            });
            const dayOut = $('.day-distance', day);
            if (dayOut) { dayOut.textContent = dayKm.toFixed(1); }
            total += dayKm;
        });
        const totalOut = $('#total-distance', form);
        if (totalOut) { totalOut.textContent = total.toFixed(1); }
    };

    function addLeg(dayId, values = {}) {
        legCounter[dayId] = (legCounter[dayId] || 0) + 1;
        const n = legCounter[dayId];
        const leg = fromTemplate(legTpl, { __DAY__: dayId, __N__: n });
        Object.entries(values).forEach(([key, value]) => {
            const input = leg.querySelector(`input[name$="[${key}]"]`);
            if (input && value !== null && value !== undefined) { input.value = value; }
        });
        $(`.services-container[data-day="${dayId}"]`, form)?.appendChild(leg);
        leg.querySelector('.remove-service-btn')?.addEventListener('click', () => {
            const day = leg.closest('.day-item');
            leg.remove();
            recalc();
            day?.querySelector('.add-service-btn')?.focus();
        });
        leg.addEventListener('change', recalc);
        attachPlaces(leg);
        return leg;
    }

    function addDay(dayId = null, legs = null) {
        dayCounter = dayId ? Math.max(dayCounter, dayId) : dayCounter + 1;
        const id = dayId || dayCounter;
        const day = fromTemplate(dayTpl, { __DAY__: id });
        daysContainer.appendChild(day);
        day.querySelector('.add-service-btn')?.addEventListener('click', () => addLeg(id));
        day.querySelector('.remove-day-btn')?.addEventListener('click', () => {
            day.remove();
            // An emptied plan starts again at day 1.
            if (!$('.day-item', daysContainer)) {
                dayCounter = 0;
            }
            recalc();
            $('[data-add-day]', form)?.focus();
        });
        (legs && legs.length ? legs : [{}]).forEach((values) => addLeg(id, values));
        return day;
    }

    $$('[data-add-day]', form).forEach((b) => b.addEventListener('click', () => {
        addDay();
        const error = $('[data-days-error]', form);
        if (error) { error.hidden = true; }
    }));

    // Rebuild the plan the visitor had typed when the server sent the form back.
    const oldDays = config.oldTourDays || {};
    if (daysContainer && Object.keys(oldDays).length) {
        Object.keys(oldDays).sort((a, b) => a - b).forEach((dayId) => {
            const legs = Object.values(oldDays[dayId]?.services || {});
            addDay(Number(dayId), legs);
        });
        recalc();
    }

    initRichEditors(form);
    initDropzones(form);
    applyMode(currentMode());
    recalc();

    const toSegments = () => {
        $$('input[data-segment]', form).forEach((input) => input.remove());
        if (form.dataset.mode !== 'tour') {
            return;
        }
        let i = 0;
        $$('.day-item', form).forEach((day) => {
            $$('.service-item', day).forEach((leg) => {
                const v = (n) => leg.querySelector(`input[name$="[${n}]"]`)?.value || '';
                const [a, b, c, d] = ['pickup_latitude', 'pickup_longitude', 'dropoff_latitude', 'dropoff_longitude'].map(v);
                const segment = {
                    day_number: day.dataset.day,
                    from_location: v('pickup_location'),
                    from_latitude: a,
                    from_longitude: b,
                    to_location: v('dropoff_location'),
                    to_latitude: c,
                    to_longitude: d,
                    departure_time: v('pickup_time'),
                    distance_km: a && b && c && d ? haversine(+a, +b, +c, +d).toFixed(2) : '',
                };
                Object.entries(segment).forEach(([key, value]) => {
                    if (value === '' || value === null) {
                        return;
                    }
                    const input = document.createElement('input');
                    input.type = 'hidden';
                    input.name = `segments[${i}][${key}]`;
                    input.value = value;
                    input.dataset.segment = '1';
                    form.appendChild(input);
                });
                i += 1;
            });
        });
    };

    /* ---------- Client details ---------- */
    const business = $('#client_type_business', form);
    const company = $('#client_company', form);
    const toggleBusiness = () => {
        const on = !!business?.checked;
        $$('.business-fields', form).forEach((el) => { el.hidden = !on; });
        $$('.individual-fields', form).forEach((el) => { el.hidden = on; });
        if (company) {
            company.required = on;
            if (!on) { company.value = ''; }
        }
        // A company's billing details go on the invoice: required, and the address opens.
        $$('[data-business-required]', form).forEach((field) => { field.required = on; });
        const billing = $('[data-billing]', form);
        if (billing && on) {
            billing.open = true;
        }
    };
    $$('input[name="client_type"]', form).forEach((radio) => radio.addEventListener('change', toggleBusiness));
    toggleBusiness();

    const createAccount = $('#create_account', form);
    const togglePassword = () => {
        const on = !!createAccount?.checked;
        $$('[data-password-field]', form).forEach((el) => {
            el.hidden = !on;
            const input = el.querySelector('input');
            if (input && !on) { input.value = ''; }
        });
    };
    createAccount?.addEventListener('change', togglePassword);
    togglePassword();

    /* ---------- Submit ---------- */
    const submitBtn = $('#submitBtn', form);
    form.addEventListener('submit', (event) => {
        event.preventDefault();
        if (form.dataset.sending === '1') {
            return;
        }
        const focusCustom = customInvalid();
        if (focusCustom) {
            show(1);
            focusCustom();
            return;
        }
        if (!form.checkValidity()) {
            const invalid = $(':invalid', form);
            if (invalid && invalid.closest('[data-step="1"]')) { show(1); }
            if (invalid) {
                markInvalid(invalid);
                invalid.reportValidity();
            }
            return;
        }
        toSegments();
        form.dataset.sending = '1';
        if (submitBtn) {
            submitBtn.disabled = true;
            submitBtn.classList.add('is-loading');
            const label = submitBtn.querySelector('[data-label]');
            if (label && submitBtn.dataset.loadingText) { label.textContent = submitBtn.dataset.loadingText; }
        }
        form.submit();
    });

    attachPlaces(form);
    $('.ui-alert--error', form.parentElement)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
}
